import React from 'react'
import { Know, KnowFormat, Know as KnowInterface, KnowTranscript } from 'interfaces/Know'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import { AnalyticsEventKnowConversionContentBannerButtonClick } from 'lib/constants/AnalyticsEventType'
import { getReferrerScreen } from 'lib/formatting/url'
import { trackEvent } from 'lib/tracking/analytics-service'
import { SectionWide } from 'style'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import theme from 'stories/utils/theme'
import _useTranslation from 'lib/hooks/_useTranslation'
import { parseLocale } from 'lib/constants/localization'
import CardContainer from 'components/elements/CardContainer'
import SideBarKnows from 'components/modules/Knows/SidebarKnows'
import { getFirstLocalSubjectFromKnowSubjects } from 'lib/formatting/subjects'
import KnowBreadcrumbs from './KnowSEOBreadcrumbs'
import KnowContent from '../Authenticated/KnowContent'
import { Flashcard } from 'interfaces/Flashcard'
import { SearchOptions } from 'interfaces/TrackingSnippets'
import Stack from 'components/elements/Stack'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import { ActiveExperiments } from 'lib/features/activeExperiments'
import DarkThemeAIEnhancedPNGPreview from './PNGPreview/DarkThemeAiEnhanced/DarkThemeAIEnhancedPNGPreview'

const DesktopContainer = styled.div`
    background-color: ${theme.colors.white};
    background-color: ${theme.colors.darkBlack};
`

const DesktopWithSidebarLayout = styled.div<{ showSidebar?: boolean }>`
    display: grid;
    background-color: ${theme.colors.darkBlack};
    grid-template-columns: ${({ showSidebar }) => (showSidebar ? '3fr 1fr' : '1000px')};
    ${({ showSidebar }) =>
        !showSidebar
            ? css`
                  place-content: center;
              `
            : ''}
    grid-gap: 50px;
    padding-top: 70px;
`

interface Props {
    know: Know
    transcript: KnowTranscript | null
    similarKnows: KnowInterface[] | null
    flashcards?: Flashcard[] | null
    search?: SearchOptions
    knowSeoInfo: KnowSeoInfo
    unblurContentForSmallerMarkets: boolean
    experiments: ActiveExperiments
}

const DesktopKnowSEOSection = ({
    know,
    transcript,
    similarKnows,
    flashcards,
    search,
    knowSeoInfo,
    unblurContentForSmallerMarkets,
    experiments,
}: Props) => {
    const router = useRouter()
    const { locale } = router
    const parsedLocale = parseLocale(locale)
    const { t } = _useTranslation('knows')

    const showPNGPreviewDesign = know.knowFormat === KnowFormat.PDF && !!know.knowDocumentPages?.length

    const referrerScreen = getReferrerScreen(router)
    const localizedSubject = know ? getFirstLocalSubjectFromKnowSubjects(know?.subjects, parsedLocale.countryCode) : null
    const showSidebar = !!similarKnows?.length

    const handleContentConversionBannerButtonClick = (platform: 'android' | 'ios') => {
        trackEvent(AnalyticsEventKnowConversionContentBannerButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })
        redirectToAppDownloadUrlAsync(platform, 'knowSeoPage', appDeepLinkKnow(know.uuid))
    }

    return (
        <DesktopContainer>
            {showPNGPreviewDesign ? (
                <DarkThemeAIEnhancedPNGPreview
                    knowSeoInfo={knowSeoInfo}
                    similarKnows={similarKnows}
                    transcript={transcript}
                    flashcards={flashcards}
                    unblurContentForSmallerMarkets={unblurContentForSmallerMarkets}
                    experiments={experiments}
                />
            ) : (
                <SectionWide>
                    <DesktopWithSidebarLayout showSidebar={showSidebar}>
                        <div>
                            <KnowBreadcrumbs knowSeoInfo={knowSeoInfo} referrerScreen={referrerScreen} />
                            <Stack gutter={10}>
                                <CardContainer
                                    hideBorderRadius
                                    hasBorder={false}
                                    hasShadow={false}
                                    padding={0}
                                    backgroundColor={theme.colors.darkBlack}
                                >
                                    <KnowContent
                                        knowSeoInfo={knowSeoInfo}
                                        isOwner={false}
                                        onContentConversionBannerButtonClick={handleContentConversionBannerButtonClick}
                                        referrerScreen={referrerScreen}
                                        downloadDeepLinkPath={appDeepLinkKnow(know.uuid)}
                                        transcript={transcript}
                                        flashcards={flashcards}
                                        search={search}
                                        similarKnows={similarKnows}
                                        isDarkTheme
                                    />
                                </CardContainer>
                            </Stack>
                        </div>

                        <aside>
                            <SideBarKnows
                                parentKnow={know}
                                heading={t('knows/similarContentHeading')}
                                knows={similarKnows ?? []}
                                subject={localizedSubject}
                            />
                        </aside>
                    </DesktopWithSidebarLayout>
                </SectionWide>
            )}
        </DesktopContainer>
    )
}

export default DesktopKnowSEOSection
